import { useEffect, useMemo, useSyncExternalStore } from 'react';

/**
 * SETUP STAYS UP UNTIL ITS DOCUMENTS ARE READY; ITS STEPS ONLY MOVE FORWARD.
 *
 * Setting up a job and processing its documents run the same five engine steps.
 * When setup finished, the folder import it starts took over the work screen and
 * its step list began again at Prepare, so the operator watched Understand/Finish
 * turn back into Prepare/Read (owner, 2026-09-29 04:20Z, job 47c6df5e). Setup now
 * ends with every step done ("Job set up: 5 document types"), and the processing it
 * starts keeps them done: a labelled phase that started again at Prepare still read
 * as the wizard going back after ten minutes of setup (owner, 2026-10-03). The
 * import's progress is the whole-folder count and bar.
 *
 * The setup wizard stays up through that processing (owner, 2026-10-03): its two-part
 * bar shows Learned, then "Read all 240 files" filling, a count and bar that only rise,
 * until the import's last wave is done (pages added, read, split, fields read and
 * indexed) and the job has no work running. Only then does the work screen open on its
 * own, with every document ready. Once the import's first batch is in the job the
 * wizard offers "Ready to check · Start checking" (SetupJourney.tsx), which leaves early
 * with the rest still processing, as closing the wizard does. A failed import does not
 * hold the wizard (the work screen offers Retry); a paused one keeps it with Resume.
 * The work screen runs (and drains the import) behind the wizard the whole time.
 *
 * PAIGE: setup reads the whole selection, so no import follows it; what the wizard waits
 * on is Paige taking the documents setup read (the job card's documents, those still
 * processing and the pages still being read), reported by `paige/setup-processing.ts`
 * from the API rather than by a work screen draining waves.
 *
 * This remembers that hand-off per job: whether the wizard still waits on it, the
 * furthest count shown, and the import it belongs to (bound by that import's id), so
 * a remount or an app restart reopens the wizard on the same count, and a later import
 * never borrows it. Display only: nothing here is sent to the engine or main, and the
 * import runs exactly as it did.
 */
export type SetupHandoff = {
  types: number;
  documents: number;
  at: number;
  importId?: string;
  /** The wizard waits on this hand-off (every hand-off since 2026-10-03; older records never hold). */
  hold?: boolean;
  /** The work screen is shown: the import finished or failed, or the operator opened the job early. */
  landed?: boolean;
  /** Files read as last shown, so a restart never shows fewer. */
  read?: number;
  /**
   * What learning found (pages, documents, types, fields, values), so the wizard that
   * opens over the job folds those results into "Learned enough to start". (Setup's
   * running time is no longer kept: the setup screen shows no running clock, October 14, 2026.)
   */
  results?: {
    id: 'pages' | 'documents' | 'types' | 'fields' | 'values';
    label: string;
    value: number | null;
  }[];
  /**
   * The reading stage is one stage across setup's own read and the files' read
   * (setup-journey-model.ts): how much of it setup's read was, so the files' read
   * carries on from there, and setup's measured time per document read, so the time
   * left is priced before the files' own pace is known.
   */
  setupWeight?: number;
  perItemMilliseconds?: number;
};
type Learned = Pick<SetupHandoff, 'results' | 'setupWeight' | 'perItemMilliseconds'>;
const storageKey = (jobId: string) => `cpv6-setup-handoff:${jobId}`;
/** An import that has not started within this long after setup is not setup's. */
const unboundLifetime = 30 * 60_000;

const read = (jobId: string): SetupHandoff | null => {
  try {
    const saved = JSON.parse(
      localStorage.getItem(storageKey(jobId)) ?? 'null',
    ) as SetupHandoff | null;
    return saved && typeof saved.at === 'number' && typeof saved.types === 'number' ? saved : null;
  } catch {
    return null;
  }
};
// The last record written per job: storage that fails still holds the wizard for this run.
const memory = new Map<string, SetupHandoff>();
const current = (jobId: string) => read(jobId) ?? memory.get(jobId) ?? null;
const listeners = new Set<() => void>();
const write = (jobId: string, saved: SetupHandoff) => {
  memory.set(jobId, saved);
  try {
    localStorage.setItem(storageKey(jobId), JSON.stringify(saved));
  } catch {
    /* storage unavailable: the hand-off only lasts this run */
  }
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;
export const setupTypesLine = (types: number) =>
  `Job set up: ${plural(types, 'document type', 'document types')}`;
export const processingTitle = (documents: number) =>
  documents > 0
    ? `Processing ${plural(documents, 'document', 'documents')}`
    : 'Processing your documents';
/** The setup wizard's heading while the documents it set up are processed. */
export const processingYourTitle = (documents: number) =>
  documents > 0
    ? `Processing your ${plural(documents, 'document', 'documents')}`
    : 'Processing your documents';

/**
 * What the setup screen last showed it had learned. The save that
 * stages the documents (leaveSetup) writes the hand-off; this hands it the screen's results.
 *
 * Each note adds to what is known, never takes it away: a screen that remounts (a manual
 * proposal Save, Read more) has not measured setup's read again, and its note without a
 * weight or a pace dropped the wizard's reading share from 30% back to 0%.
 */
const learnedByJob = new Map<string, Learned>();
export function noteSetupResults(jobId: string, learned: Learned) {
  const known = learnedByJob.get(jobId);
  if (!known) {
    learnedByJob.set(jobId, learned);
    return;
  }
  const found = (results: Learned['results']) =>
    (results ?? []).filter((tile) => tile.value !== null).length;
  learnedByJob.set(jobId, {
    results:
      found(learned.results) >= found(known.results)
        ? (learned.results ?? known.results)
        : known.results,
    setupWeight: Math.max(known.setupWeight ?? 0, learned.setupWeight ?? 0) || undefined,
    perItemMilliseconds: learned.perItemMilliseconds ?? known.perItemMilliseconds,
  });
}
/**
 * A setup run that was cleared or started again takes what it learned along. Kept, the
 * next run's notes merged over it, so an abandoned setup's reading share, time and results
 * were the next one's. Read more is the same setup carrying on, so it keeps them.
 */
export function forgetSetupResults(jobId: string) {
  learnedByJob.delete(jobId);
}

/**
 * How many files the operator chose for setup, counted when they chose them. While setup
 * learns from a sample, the folder's own status does not carry the whole folder's count yet,
 * and the bar's second part still says "Read all 140 files" from the first second (seen in
 * the real app, October 3, 2026: it said "Read all your files"). Kept per job, for the
 * storage's life, so a remount or restart mid-setup still has it.
 */
const filesKey = (jobId: string) => `cpv6-setup-files:${jobId}`;
const filesMemory = new Map<string, number>();
export function noteSetupFiles(jobId: string, files: number) {
  if (!Number.isFinite(files) || files <= 0) return;
  filesMemory.set(jobId, files);
  try {
    localStorage.setItem(filesKey(jobId), String(files));
  } catch {
    /* this run only */
  }
}
export function setupFiles(jobId: string): number | null {
  try {
    const saved = Number(localStorage.getItem(filesKey(jobId)));
    if (Number.isFinite(saved) && saved > 0) return saved;
  } catch {
    /* fall back to this run's count */
  }
  return filesMemory.get(jobId) ?? null;
}

/** Setup has finished and its documents are staged: the wizard waits until they are processed. */
export function rememberSetupHandoff(
  jobId: string,
  types: number,
  documents: number,
  learned: Learned = learnedByJob.get(jobId) ?? {},
) {
  const saved = current(jobId);
  if (
    saved &&
    saved.hold &&
    !saved.landed &&
    !saved.importId &&
    saved.types === types &&
    saved.documents === documents
  )
    return;
  write(jobId, { types, documents, at: Date.now(), hold: true, read: 0, ...learned });
}

/**
 * The last page the setup screen showed, kept per job. A remount after the files' pages were
 * read (a reload, a restart, switching jobs and back) had no page left to show, and the page
 * area sat on "Opening your files" while the values were still being pulled (owner, October
 * 4, 2026: "the left screen shouldn't be empty"). The screen starts from this page instead.
 */
const pageKey = (jobId: string) => `cpv6-setup-page:${jobId}`;
const pageMemory = new Map<string, string>();
export function noteSetupPage(jobId: string, previewPath: string) {
  if (!previewPath || pageMemory.get(jobId) === previewPath) return;
  pageMemory.set(jobId, previewPath);
  try {
    localStorage.setItem(pageKey(jobId), previewPath);
  } catch {
    /* this run only */
  }
}
export function setupPage(jobId: string): string | null {
  try {
    const saved = localStorage.getItem(pageKey(jobId));
    if (saved) return saved;
  } catch {
    /* fall back to this run's page */
  }
  return pageMemory.get(jobId) ?? null;
}
/** A cleared setup (Start over, choosing the documents again) takes its page along: the next setup opened on it. */
export function forgetSetupPage(jobId: string) {
  pageMemory.delete(jobId);
  try {
    localStorage.removeItem(pageKey(jobId));
  } catch {
    /* nothing was kept */
  }
}

/** The wizard is done with this hand-off: the work screen is shown from now on. */
export function landSetupHandoff(jobId: string) {
  const saved = current(jobId);
  if (saved?.hold && !saved.landed) write(jobId, { ...saved, landed: true });
}

/** The furthest count the wizard has shown, kept for a restart. */
export function noteSetupHandoffRead(jobId: string, files: number) {
  const saved = current(jobId);
  if (saved?.hold && !saved.landed && files > (saved.read ?? 0))
    write(jobId, { ...saved, read: files });
}

/** The hand-off the setup wizard still waits on for this job, or null once it has landed. */
export function heldSetupHandoff(jobId: string): SetupHandoff | null {
  const saved = current(jobId);
  return saved?.hold && !saved.landed ? saved : null;
}

/** heldSetupHandoff, kept current on screen as the hand-off changes. */
export function useHeldSetupHandoff(jobId: string): SetupHandoff | null {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => JSON.stringify(heldSetupHandoff(jobId)),
    () => 'null',
  );
  return useMemo(() => JSON.parse(snapshot) as SetupHandoff | null, [snapshot]);
}

/**
 * What the work screen (running behind the wizard) reports of the import, for the
 * wizard's count and for knowing when every document is done.
 */
export type SetupImportReport = {
  /** The staged queue has been read since this work screen mounted. */
  loaded: boolean;
  /** A wave is waiting or being read. */
  busy: boolean;
  /** A wave failed and waits on Retry or Discard. */
  failed: boolean;
  /** The folder being added, counted whole by this run (folder-import-progress.ts); null between waves. */
  total: number | null;
  read: number;
  /** Files still queued (waiting or being read), the measure that survives a restart. */
  queuedFiles: number;
  /** Documents in the job, ready to check. */
  documents: number;
  /** Files in the import's first batch (its first two waves), when the waves are known. */
  firstBatchFiles: number | null;
  /** The first batch is read and in the job: the wizard offers "Start checking". */
  firstBatchReady: boolean;
  live: string | null;
  previewPath: string | null;
  remainingMilliseconds: number | null;
};

/**
 * The finished setup `importId` follows, from the hand-off as it is now: bound to that import,
 * or not bound yet and recent enough to be setup's. Null when this import is not the one setup
 * started.
 */
export function setupHandoffFor(
  jobId: string,
  importId: string | null,
  now = Date.now(),
): SetupHandoff | null {
  const saved = importId ? current(jobId) : null;
  return saved &&
    importId &&
    (saved.importId ? saved.importId === importId : now - saved.at < unboundLifetime)
    ? saved
    : null;
}

/** The import setup started is this one: from now on the hand-off belongs to it, however long it runs. */
export function bindSetupHandoff(jobId: string, importId: string) {
  const saved = setupHandoffFor(jobId, importId);
  if (saved && !saved.importId) write(jobId, { ...saved, importId });
}

/**
 * setupHandoffFor, kept current on screen. It reads the live hand-off, not the one there when
 * the import first showed: that one was not bound yet, so half an hour into the same import it
 * no longer looked like setup's, and the steps fell from "complete" back to the import's own
 * stage (owner, 2026-10-03: they only move forward).
 */
export function useSetupHandoff(jobId: string, importId: string | null): SetupHandoff | null {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => JSON.stringify(setupHandoffFor(jobId, importId)),
    () => 'null',
  );
  const saved = useMemo(() => JSON.parse(snapshot) as SetupHandoff | null, [snapshot]);
  useEffect(() => {
    if (saved && !saved.importId && importId) bindSetupHandoff(jobId, importId);
  }, [saved, importId, jobId]);
  return saved;
}
